<template>
  <div class="mc-loads-table">
    <div class="clearfix">
      <div class="d-flex mc-list-header">
        <h4 class="card-title flex-grow-1">{{ $t('maintenance.order.partsList') }}</h4>
        <McButton
          v-if="$route.params.id && selectedIdsLength"
          variant="secondary-red"
          class="mx-2 ms-auto"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          v-if="$route.params.id && (
            isMGMAndMO ? ['Draft', 'Verification', 'InProgress', 'Confirmed'].includes(status)
            : status === 'Draft' || status === 'Verification'
          )"
          variant="primary"
          class="ms-auto"
          :text="$t('global.create')"
          icon="icon-fill_plus"
          :disabled="disabledGlobal"
          @click="handleAdd"
        />
      </div>
    </div>

    <b-card>
      <b-row align-h="end">
        <b-col md="8" xl="10">
          <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            {{ $t('global.object.selectedRow') }}
            <a class="color-primary" @click="handleUnselectAll">{{
              $t('global.action.deselect')
            }}</a>
          </h6>
          <McAlert
            v-if="invalidKeysLength && !selectedIdsLength"
            variant="error"
            text="Baris data error."
          >
            <template #prefix>
              <div class="mc-alert__custom_prefix">
                <p>{{ invalidKeysLength }}</p>
              </div>
            </template>
          </McAlert>
        </b-col>
        <b-col md="4" xl="2">
          <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
            <template #prepend>
              <i class="ps-2 mc-icon icon-fill_search"></i>
            </template>
            <b-form-input
              ref="searchInput"
              :placeholder="`${$t('global.search')}...`"
              :disabled="disabledGlobal"
              :value="search"
              @input="handleSearchChange"
            />
            <template v-if="search" #append>
              <button
                class="clear-search-btn"
                :disabled="disabledGlobal"
                @click="handleClearSearch"
              >
                <i class="pe-2 mc-icon icon-outline_close"></i>
              </button>
            </template>
          </b-input-group>
        </b-col>
      </b-row>

      <FormulateForm
        ref="formRef"
        v-model="formValues"
        name="editable-table-form"
        :errors="formulateFormErrors"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <div class="table-responsive border border-1 mc-loads-table__editable-table">
          <b-table
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :per-page="limit"
            fixed
            show-empty
            foot-clone
            class="editable-table"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2" />
                <strong>{{ $t('global.text.loading') }}...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">{{ $t('global.emptyTable') }}</div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template #head(checkbox)>
              <McCheckbox
                v-model="selectedAll"
                :disabled="disabledGlobal"
                @change="handleSelectAll"
              />
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div class="d-flex justify-content-between" @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div>
                    <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                      <i class="mc-icon icon-outline_chevron-up"></i>
                    </div>
                    <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                      <i class="mc-icon icon-outline_chevron-down"></i>
                    </div>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="data">
              <McCheckbox
                :checked="selectedIds[data.item.partId]"
                :disabled="disabledGlobal"
                @change="(event) => handleSelect(event, data)"
              />
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                <template v-if="field.type === 'select-resourceful-v2'">
                  <FormulateInput
                    v-bind="{ ...field }"
                    :fullObject="fullObjects[field.name]"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    validation-behavior="live"
                    error-behavior="value"
                    size="sm"
                    @update:fullObject="($event) => setFullObject(field.name, $event)"
                    @addTag="customTag"
                  />
                </template>

                <template v-else>
                  <FormulateInput
                    v-bind="{ ...field }"
                    :fullObject="fullObjects[field.name]"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    validation-behavior="live"
                    error-behavior="value"
                    size="sm"
                    @update:fullObject="($event) => (fullObjects[field.name] = $event)"
                  />
                </template>
              </template>

              <!-- Bold First Column -->
              <template v-else-if="field.isRedirectable">
                <div class="fw-bold text-primary">
                  {{ value }}
                </div>
              </template>

              <!-- Show Only Column -->
              <template v-else-if="!field.isEditable && isEditingRow == index">
                <div v-if="render">
                  {{ showOnlyValue(value, field) }}
                </div>
              </template>

              <template v-else>
                <div
                  class="td__overflow-ellipsis"
                  :class="
                    invalidItems[item.partId] && invalidItems[item.partId][field.name]
                      ? 'td__invalid-data'
                      : undefined
                  "
                >
                  {{ transformCellValue(value, field) }}
                </div>
              </template>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template #cell(action)="data">
              <div v-if="isEditingRow == data.index">
                <b-button variant="outline" class="__accept-button btn" type="submit">
                  <i class="mc-icon icon-fill_checkmark"></i>
                </b-button>

                <b-button
                  variant="outline"
                  class="__cancel-button btn"
                  @click="handleEditCancel(data)"
                >
                  <i class="mc-icon icon-outline_close"></i>
                </b-button>
              </div>

              <b-button
                v-else
                variant="outline"
                :class="{
                  '__edit-button btn': true,
                  __disabled: isEditingRow !== null || isBusy,
                }"
                @click="handleEditStart(data)"
              >
                <i class="mc-icon icon-outline_edit"></i>
              </b-button>
            </template>

            <template #foot(checkbox)>
              <b-td colspan="2" class="mc-footer"> {{ $t('global.text.total') }} </b-td>
            </template>

            <template #foot()="{ column }">
              <b-td class="mc-footer w-100">
                <div v-if="column === 'part' && status !== 'Draf'">
                  {{ $t('global.text.total') }}
                </div>
                <div v-else-if="column === 'totalPrice'">
                  <template v-if="render">
                    {{ `Rp ${thousandFormat(totalPrice)}` }}
                  </template>
                </div>
              </b-td>
            </template>
          </b-table>
        </div>
      </FormulateForm>

      <div class="d-flex flex-wrap flex-md-nowrap">
        <div class="mc-limit">
          <div class="__limit">
            <div>{{ $t('global.show') }}</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              :value="limit"
              @input="handleLimitChange"
            />
            <div>{{ $t('global.object.data') }}.</div>
          </div>

          <div v-if="totalCount === 0" class="__info">
            <strong>{{ totalCount }}</strong> {{ $t('global.object.from') }}
            <strong>{{ totalCount }}</strong> {{ $t('global.object.data') }}.
          </div>

          <div v-else class="__info">
            <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
            {{ $t('global.object.from') }} <strong>{{ totalCount }}</strong>
            {{ $t('global.object.data') }}.
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            :value="page"
            @change="handlePageChange"
          />
        </div>
      </div>

      <b-modal
        id="mc-modal-delete"
        ref="mc-modal-delete"
        class="mc-modal"
        :title="$t('global.delete')"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
            {{ $t('global.confirmDelete2') }}
            ?
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import { Decimal } from 'decimal.js'
import unitOfMeasures from '../lib/units'
import { GET_PARTS } from '@/services/api/master/spareparts'
import McCheckbox from '@/components/McCheckbox'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McAlert,
    McCheckbox,
  },

  mixins: [Helper],
  props: {
    apiClient: { required: true },
    status: { type: String },
    limitOptions: {
      default: () => [5, 10, 25, 50, 100],
    },
    // invalidFields: { type: Array },
    service2SoId: { type: Number },
    fieldOptions: {},
    listDatas: [],
  },
  data() {
    return {
      unitOfMeasures,
      isBusy: true,
      isSubmitting: false,
      limit: this.limitOptions[0] ?? 10,
      page: 1,
      totalCount: 0,
      ids: [],
      items: [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},

      loadsData: {},
      showResponse: {},
      invalidItems: {},
      formulateFormErrors: {},
      fullObjects: {},
      render: true,
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      const fields = [
        {
          name: 'part',
          key: 'part.name',
          label: this.$t('maintenance.order.form.partsName'),
          placeholder: `${this.$t('global.form.select')} ${this.$t(
            'maintenance.order.form.partsName'
          )}`,
          modalTitle: this.$t('maintenance.order.form.partsName'),
          isEditable: true,
          isSortable: true,
          validation: 'required',
          type: 'value-help-v2',
          listGetter: this.resourcefulQuery.parts,
          tableFields: [
            {
              key: 'code',
              thClass: 'mc-table--50',
              label: this.$t('master.parts.sparePartCode'),
              sortable: true,
              isRedirectable: true,
            },
            {
              key: 'name',
              thClass: 'mc-table--50',
              label: this.$t('master.parts.sparePartName'),
              sortable: true,
            },
            {
              key: 'partType.partCategory.category',
              thClass: 'mc-table--50',
              label: this.$t('master.parts.category'),
              sortable: true,
            },
            {
              key: 'partType.type',
              thClass: 'mc-table--50',
              label: this.$t('master.parts.sparePartType'),
              sortable: true,
            },
          ],
          displayedBy: 'name',
          trackBy: 'partId',
          thStyle: 'width: 250px',
        },
        {
          name: 'type',
          key: 'part.partType.type',
          label: this.$t('global.object.type'),
          isSortable: false,
          isEditable: false,
          thStyle: 'width: 150px',
        },
        {
          name: 'category',
          key: 'part.partType.partCategory.category',
          label: this.$t('master.parts.category'),
          isSortable: false,
          isEditable: false,
          thStyle: 'width: 130px',
        },
        {
          name: 'qty',
          key: 'qty',
          label: this.$t('maintenance.order.form.qty'),
          placeholder: this.$t('maintenance.order.form.qty'),
          isEditable: true,
          isSortable: false,
          validation: 'required|number',
          thStyle: 'width: 130px',
        },
        {
          name: 'unit',
          key: 'unit',
          label: this.$t('maintenance.order.form.unit'),
          placeholder: `${this.$t('global.form.select')} ${this.$t('global.object.or')} ${this.$t(
            'global.create'
          )} ${this.$t('maintenance.order.form.unit')}`,
          type: 'select-resourceful-v2',
          optionsGetter: this.resourcefulQuery.units,
          trackBy: 'value',
          optionLabelFn: ({ item }) => item.label,
          isEditable: true,
          isSortable: false,
          validation: 'required',
          multiple: false,
          taggable: true,
          thStyle: 'width: 130px',
        },
        {
          name: 'price',
          key: 'price',
          label: this.$t('maintenance.order.form.unitPrice'),
          placeholder: this.$t('maintenance.order.form.unitPrice'),
          isEditable: true,
          isSortable: false,
          validation: 'required|number',
          thStyle: 'width: 130px',
        },
        {
          name: 'discount',
          key: 'discount',
          label: this.$t('maintenance.order.form.discount'),
          placeholder: this.$t('maintenance.order.form.discount'),
          isEditable: true,
          isSortable: false,
          thStyle: 'width: 100px',
          validation: 'number',
        },
        {
          name: 'totalPrice',
          key: 'totalPrice',
          label: this.$t('maintenance.order.form.totalPrice'),
          placeholder: this.$t('maintenance.order.form.totalPrice'),
          isSortable: false,
          isEditable: false,
          thStyle: 'width: 130px',
        },
        {
          name: 'note',
          key: 'note',
          label: this.$t('maintenance.order.form.note'),
          placeholder: this.$t('maintenance.order.form.note'),
          isEditable: true,
          isSortable: false,
          thStyle: 'width: 130px',
        },
      ]
      if (
        this.isMGMAndMO ?
        !['Draft', 'Verification', 'InProgress', 'Confirmed'].includes(this.status)
        :this.status !== 'Draft' && this.status !== 'Verification'
      ) return fields
      else
        return [
          {
            key: 'checkbox',
            label: '',
            thStyle: 'width: 40px',
          },
          ...fields,
          {
            key: 'action',
            label: '',
            thClass: 'mc-action-col-head',
            tdClass: 'mc-action-cell',
          },
        ]
    },
    resourcefulQuery() {
      return {
        parts: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_PARTS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const param = this.items.map((i) => i.partId)

          return {
            metadata: {
              total_count: res.data.parts.parts.pagination.total,
              page: res.data.parts.parts.pagination.page,
            },
            data: {
              paginated_result: res.data.parts.parts.data.filter((x) => !param.includes(x.partId)),
            },
          }
        },
        units: (opts) => {
          let unitOfMeasures = this.unitOfMeasures

          if (opts?.search) {
            unitOfMeasures = unitOfMeasures.filter((item) =>
              this.isContains(opts.search, item.label)
            )
          }

          return {
            metadata: {
              total_page: 1,
              total_count: 5,
              page: 1,
            },
            data: {
              paginated_result: unitOfMeasures,
            },
          }
        },
      }
    },
    totalPrice() {
      let totalPrice = 0

      this.items.forEach((item, index) => {
        if (this.isEditingRow == index) {
          totalPrice +=
            Number(this.formValues.price) * Number(this.formValues.qty) -
            (Number(this.formValues.discount) / 100) *
              (Number(this.formValues.price) * Number(this.formValues.qty))
        } else {
          totalPrice += item.price * item.qty - (item.discount / 100) * (item.price * item.qty)
        }
      })

      return totalPrice ?? '-'
    },

    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
  },
  watch: {
    async formValues() {
      if (this.isEditingRow == null && !this.invalidKeysLength) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(this.formValues, this.loadsData)

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // delete the field in formulateFormErrors
        this.$delete(this.formulateFormErrors, field)
      })
    },
    'formValues.part': async function (newValue, oldValue) {
      if (newValue === oldValue) {
        return
      }

      if (newValue !== oldValue) {
        if (!newValue) return

        const checkData = await this.apiClient.getById(newValue, this.service2SoId)
        const valueData = this.listDatas.filter((item) => item.partId == newValue)
        let data = checkData ?? valueData[0]

        const unit = data.unit ?? data.part.unit
        const type = checkData ? data.part.partType.type : data.partType.type
        const category = checkData
          ? data.part.partType.partCategory.category
          : data.partType.partCategory.category

        this.$set(this.formValues, 'unit', unit)
        this.fullObjects.unit = {
          label: unit.charAt(0).toUpperCase() + unit.slice(1),
          value: unit,
        }

        this.$set(this.formValues, 'price', data.price)
        this.$set(this.formValues, 'qty', data.qty ?? 1)
        this.$set(this.formValues, 'discount', data.discount ?? 0)
        this.$set(this.items[this.isEditingRow], 'part', {
          partType: {
            type: type,
            partCategory: {
              category: category,
            },
          },
          price: 'Rp 0',
        })

        this.calculateTotalPrice(
          this.formValues.qty,
          this.formValues.price,
          this.formValues.discount
        )
      }
    },
    'formValues.qty': function (newValue, oldValue) {
      if (newValue === oldValue) return

      this.calculateTotalPrice(this.formValues.qty, this.formValues.price, this.formValues.discount)
    },
    'formValues.price': function (newValue, oldValue) {
      if (newValue === oldValue) return

      this.calculateTotalPrice(this.formValues.qty, this.formValues.price, this.formValues.discount)
    },
    'formValues.discount': function (newValue, oldValue) {
      if (newValue === oldValue) {
        return
      }

      this.calculateTotalPrice(this.formValues.qty, this.formValues.price, this.formValues.discount)
    },
    items: {
      handler: debounce(async function () {
        this.render = false
        await this.$nextTick()
        this.render = true
      }, 500),
      deep: true,
      immediate: true,
    },
  },

  async mounted() {
    this.computedFields.forEach((el) => {
      if (el.isSortable) el._sort = 'desc'
      if (el.orderBy) this.sorts.push({ [el.key]: el.orderBy })
    })

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
      sorts: this.stringSorts(),
    })
  },

  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    stringSorts() {
      return this.sorts.reduce((next, ob) => {
        const str = JSON.stringify(ob)
        return [...next, str]
      }, [])
    },
    handleFailedValidation() {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    showOnlyValue(value, field) {
      if (!this.formValues.part || !value) return `- ${field.afterText ?? ''}`

      return `${value} ${field.afterText ?? ''}`
    },
    transformCellValue(value, field) {
      if (field.name === 'price' || field.name === 'totalPrice')
        return value ? `Rp ${this.thousandFormat(value)}` : 'Rp 0'

      if (field.name === 'unit') return value.charAt(0).toUpperCase() + value.slice(1) ?? ''
      if (value === false) return `- ${field.afterText ?? ''}`
      if (typeof value === 'number') return `${value.toLocaleString()} ${field.afterText ?? ''}`
      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    AssignInvalidErrors() {
      if (!this.invalidFields.length) return
      const indexObject = {}

      // format the invalidFields from array to object with id as the key
      for (const load of this.invalidFields) {
        const loadObj = {}

        for (const field in load) {
          if (field === 'id') continue
          loadObj[field] = ['Data telah dihapus dari sistem.']
        }

        indexObject[load.partId] = loadObj
      }

      // assign to invalidItems as state that could be used to show error
      this.invalidItems = indexObject
    },
    async getData(opts) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      const filter = {
        pagination: {
          page: opts.page,
          show: opts.limit,
        },
        search: opts.search,
        service2SoId: this.service2SoId,
      }

      this.isBusy = true
      const res = await this.apiClient.get(filter)

      if (!res) return

      this.items = res.data

      this.ids = res.data.map((x) => x.partId)
      this.page = res.pagination.page
      this.totalCount = res.pagination.total

      this.isBusy = false
    },

    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
    },

    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      })
    },

    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
    },

    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },

    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.getById(data.item.partId, this.service2SoId)
      if (!refetchedData) return

      const loadData = refetchedData

      // assign formValues with response show data
      this.computedFields.forEach((field) => {
        if (!field.isEditable) return
        let value

        // assign for the product_id
        if (field.name === 'part') value = loadData.partId
        else value = loadData[field.name]

        this.fullObjects.part = loadData.part
        this.$set(this.formValues, field.name, value)
      })

      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.partId == data.item.partId) return loadData
        return item
      })

      this.isEditingRow = data.index
      this.showResponse = { ...loadData }
      this.loadsData = this.formValues

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[loadData.partId]) {
        this.formulateFormErrors = { ...this.invalidItems[loadData.partId] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formulateFormErrors = {}
      this.formValues = {}
      this.fullObjects = {}
      this.isEditingRow = null
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data, this.service2SoId)
      if (!createdData) return false

      if (!createdData.isSuccessful) this.$mcToast.warning('Data tidak berhasil ditambahkan')

      this.$mcToast.success('Data berhasil ditambahkan')

      const refetchedData = createdData.createPart2Service
      if (!refetchedData) return false

      this.fullObjects.part = refetchedData.part
      this.ids.push(refetchedData.partId)
      this.totalCount += 1

      return {
        ...data,
        ...refetchedData,
      }
    },
    async submitUpdate(data, oldData) {
      const [patchData, hasChanged] = this.filterUnchangedData(data, oldData)

      if (!hasChanged) {
        this.isSubmitting = false
        this.handleEditCancel({ index: this.isEditingRow })
        return
      }

      const updatedData = await this.apiClient.update(
        oldData.partId,
        oldData,
        data,
        this.service2SoId
      )

      if (!updatedData) return false

      this.loadsData = data

      const refetchedData = updatedData.updatePart2Service

      if (!updatedData.isSuccessful) {
        this.$mcToast.warning('Perubahan data tidak berhasil disimpan')
        return false
      }

      if (this.invalidItems[oldData.partId]) {
        this.$delete(this.invalidItems, oldData.partId)
        this.formulateFormErrors = {}
      }

      this.$mcToast.success('Perubahan data berhasil disimpan')

      return {
        ...refetchedData,
      }
    },
    async handleSubmit(data) {
      this.isBusy = true
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formulateFormErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        this.isBusy = false
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.computedFields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }
      // If newData is false, it means there is an error
      if (newData === false) {
        this.isEditingRow = null
        this.isSubmitting = false
        this.isBusy = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
      this.isBusy = false
      this.formValues = {}
      this.fullObjects = {}
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key, this.service2SoId))
        }
      }
      await Promise.all(promises)
      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: 1,
      })

      for (const selectedid in this.selectedIds) {
        if (this.invalidItems[selectedid]) {
          this.$delete(this.invalidItems, selectedid)
        }
      }

      await this.$nextTick()
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.partId]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [
        {
          partId: 0,
          part: {
            unit: '',
            partType: {
              type: null,
              partCategory: {
                category: null,
              },
            },
            price: 0,
          },
          price: 0,
          qty: 0,
          discount: 0,
          totalPrice: 0,
        },
        ...this.items,
      ]

      this.formValues.price = 0
      this.formValues.qty = 0
      this.formValues.discount = 0
    },

    customTag(value) {
      const tag = {
        label: value,
        value: value.toLowerCase(),
      }

      this.setFullObject('unit', tag)

      this.formValues.unit = tag.value
    },

    setFullObject(field, value) {
      this.fullObjects[field] = value
    },

    calculateTotalPrice(qty, price, discount) {
      const convertData = {
        qty: qty,
        price: price ? new Decimal(price) : 0,
        discount: discount,
      }

      let totalPrice = convertData.price

      if (discount) {
        const discountPrice = totalPrice * (convertData.discount / 100)
        totalPrice = totalPrice - discountPrice
      }
      totalPrice = totalPrice * convertData.qty

      if (totalPrice.d === null || isNaN(totalPrice)) return

      this.$set(
        this.items[this.isEditingRow],
        'totalPrice',
        `Rp ${this.thousandFormat(totalPrice)}`
      )
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
